// The arcade cabinets in the back room, next to the slot machine: a quick
// Minesweeper and a Stacker. Both are free to play and pay a few coins. The
// server runs the games (the minefield is secret, Stacker positions come from
// the server's clock); the client only draws them. Shared so both agree.
import { z } from "zod";
import { secureRng, type Rng } from "./rng";

// ---------- limits ----------

/** Arcade moves (reveal, flag, drop, new game) per second per account. */
export const ARCADE_MOVES_PER_SECOND = 12;
/** New arcade games per minute per account. */
export const ARCADE_NEW_GAMES_PER_MINUTE = 12;

// ---------- minesweeper ----------

export const MINES_COLS = 6;
export const MINES_ROWS = 8;
export const MINES_CELLS = MINES_COLS * MINES_ROWS;
export const MINES_BOMBS = 6;
/** Coins for clearing the field. */
export const MINES_WIN_COINS = 75;

/** Cell codes in a minesweeper view: 0..8 = revealed (neighbour count). */
export const MINE_HIDDEN = -1;
export const MINE_FLAG = -2;
/** A mine shown after the game ends. */
export const MINE_BOMB = 9;
/** The mine you stepped on. */
export const MINE_BOOM = 10;

export type MinesStatus = "playing" | "won" | "lost";

/** Indices of the cells around cell i (not i itself). */
export function minesNeighbours(i: number): number[] {
  const r = Math.floor(i / MINES_COLS);
  const c = i % MINES_COLS;
  const out: number[] = [];
  for (let dr = -1; dr <= 1; dr++) {
    for (let dc = -1; dc <= 1; dc++) {
      if (dr === 0 && dc === 0) continue;
      const rr = r + dr;
      const cc = c + dc;
      if (rr >= 0 && rr < MINES_ROWS && cc >= 0 && cc < MINES_COLS) out.push(rr * MINES_COLS + cc);
    }
  }
  return out;
}

/**
 * Lay the mines after the first click: never on the clicked cell or around it,
 * so the first click always opens an area. Returns sorted cell indices.
 */
export function layMines(safeCell: number, rng: Rng = secureRng): number[] {
  const banned = new Set([safeCell, ...minesNeighbours(safeCell)]);
  const pool: number[] = [];
  for (let i = 0; i < MINES_CELLS; i++) if (!banned.has(i)) pool.push(i);
  // Partial Fisher-Yates: the first MINES_BOMBS picks are uniform.
  for (let k = 0; k < MINES_BOMBS; k++) {
    const j = k + rng.int(pool.length - k);
    [pool[k], pool[j]] = [pool[j]!, pool[k]!];
  }
  return pool.slice(0, MINES_BOMBS).sort((a, b) => a - b);
}

/** The whole minesweeper game as the server keeps it (the mines are never sent while playing). */
export const MinesStateSchema = z.object({
  id: z.string(),
  /** Mine cells; null until the first reveal. */
  mines: z.array(z.number().int().min(0).max(MINES_CELLS - 1)).nullable(),
  revealed: z.array(z.boolean()).length(MINES_CELLS),
  flagged: z.array(z.boolean()).length(MINES_CELLS),
  status: z.enum(["playing", "won", "lost"]),
  /** The mine that went off, if lost. */
  boom: z.number().int().nullable(),
});
export type MinesState = z.infer<typeof MinesStateSchema>;

export function newMines(id: string): MinesState {
  return {
    id,
    mines: null,
    revealed: Array<boolean>(MINES_CELLS).fill(false),
    flagged: Array<boolean>(MINES_CELLS).fill(false),
    status: "playing",
    boom: null,
  };
}

function mineCount(mines: ReadonlySet<number>, i: number): number {
  return minesNeighbours(i).filter((n) => mines.has(n)).length;
}

/** Reveal a cell (flood-filling empty areas). Returns the new state; s is not changed. */
export function revealMine(s: MinesState, cell: number, rng: Rng = secureRng): MinesState {
  if (s.status !== "playing" || cell < 0 || cell >= MINES_CELLS || s.revealed[cell] || s.flagged[cell]) return s;
  const next: MinesState = { ...s, revealed: [...s.revealed], flagged: [...s.flagged] };
  if (!next.mines) next.mines = layMines(cell, rng);
  const mines = new Set(next.mines);
  if (mines.has(cell)) {
    next.status = "lost";
    next.boom = cell;
    return next;
  }
  const stack = [cell];
  while (stack.length > 0) {
    const i = stack.pop()!;
    if (next.revealed[i] || mines.has(i)) continue;
    next.revealed[i] = true;
    next.flagged[i] = false;
    if (mineCount(mines, i) === 0) for (const n of minesNeighbours(i)) if (!next.revealed[n]) stack.push(n);
  }
  const open = next.revealed.filter(Boolean).length;
  if (open === MINES_CELLS - MINES_BOMBS) next.status = "won";
  return next;
}

/** Put a flag on a hidden cell, or take it off. */
export function flagMine(s: MinesState, cell: number): MinesState {
  if (s.status !== "playing" || cell < 0 || cell >= MINES_CELLS || s.revealed[cell]) return s;
  const flagged = [...s.flagged];
  flagged[cell] = !flagged[cell];
  return { ...s, flagged };
}

export const MinesViewSchema = z.object({
  id: z.string(),
  /** One code per cell, row by row (MINE_HIDDEN, MINE_FLAG, 0..8, MINE_BOMB, MINE_BOOM). */
  cells: z.array(z.number().int().min(-2).max(10)).length(MINES_CELLS),
  status: z.enum(["playing", "won", "lost"]),
});
export type MinesView = z.infer<typeof MinesViewSchema>;

/** What the player may see: mines only once the game is over. */
export function minesView(s: MinesState): MinesView {
  const mines = new Set(s.mines ?? []);
  const over = s.status !== "playing";
  const cells = s.revealed.map((open, i) => {
    if (open) return mineCount(mines, i);
    if (over && mines.has(i)) return i === s.boom ? MINE_BOOM : MINE_BOMB;
    return s.flagged[i] ? MINE_FLAG : MINE_HIDDEN;
  });
  return { id: s.id, cells, status: s.status };
}

// ---------- stacker ----------

export const STACKER_COLS = 7;
export const STACKER_LEVELS = 10;
/**
 * Coins for reaching a level (rows placed). You get the highest milestone you
 * reach: each is paid the moment you reach it, as the difference from the one
 * before, so a game that ends (or is abandoned) has already paid in full.
 */
export const STACKER_REWARDS: Readonly<Record<number, number>> = { 4: 25, 6: 75, 8: 150, 10: 300 };
/**
 * A drop message may be dated up to this long before the server receives it
 * (network delay). Older claims are moved up to this limit.
 */
export const STACKER_LAG_MS = 300;

/** Widest the moving row can be on a level (the top rows get narrower). */
export function stackerMaxWidth(level: number): number {
  return level <= 5 ? 3 : level <= 8 ? 2 : 1;
}

/** Milliseconds per one-cell step on a level (each level is faster). */
export function stackerStepMs(level: number): number {
  return Math.round(220 * 0.9 ** (level - 1));
}

/** Left column of the moving row `elapsedMs` after its level started (it bounces between the walls). */
export function stackerPosition(level: number, width: number, elapsedMs: number): number {
  const range = STACKER_COLS - width;
  if (range <= 0) return 0;
  const steps = Math.floor(Math.max(0, elapsedMs) / stackerStepMs(level));
  const k = steps % (2 * range);
  const p = k <= range ? k : 2 * range - k;
  // Odd levels start on the left, even ones on the right.
  return level % 2 === 1 ? p : range - p;
}

/** Total coins for reaching `rows` rows (the highest milestone at or below it). */
export function stackerReward(rows: number): number {
  let best = 0;
  for (const [lvl, coins] of Object.entries(STACKER_REWARDS)) if (rows >= Number(lvl)) best = Math.max(best, coins);
  return best;
}

export const StackerRowSchema = z.object({ left: z.number().int(), width: z.number().int() });
export type StackerRow = z.infer<typeof StackerRowSchema>;

/** The part of a dropped row that sits on the row below (width 0 = missed). */
export function stackerOverlap(below: StackerRow | null, dropped: StackerRow): StackerRow {
  if (!below) return dropped;
  const left = Math.max(below.left, dropped.left);
  const right = Math.min(below.left + below.width, dropped.left + dropped.width);
  return right > left ? { left, width: right - left } : { left: dropped.left, width: 0 };
}

export const StackerStateSchema = z.object({
  id: z.string(),
  /** Rows placed so far, bottom first. */
  rows: z.array(StackerRowSchema).max(STACKER_LEVELS),
  /** Width of the moving row. */
  width: z.number().int().min(0).max(STACKER_COLS),
  /** Server time the moving row started (ms). */
  levelStartAt: z.number(),
  status: z.enum(["playing", "over", "won"]),
  /** The last row dropped, before it was cut (for drawing). */
  last: StackerRowSchema.nullable(),
});
export type StackerState = z.infer<typeof StackerStateSchema>;

export function newStacker(id: string, now: number): StackerState {
  return { id, rows: [], width: stackerMaxWidth(1), levelStartAt: now, status: "playing", last: null };
}

/**
 * Drop the moving row. `claimedElapsed` is how long the player saw it moving;
 * the server only accepts a claim between (its own elapsed - STACKER_LAG_MS)
 * and its own elapsed.
 */
export function dropStacker(s: StackerState, claimedElapsed: number, now: number): StackerState {
  if (s.status !== "playing") return s;
  const level = s.rows.length + 1;
  const real = Math.max(0, now - s.levelStartAt);
  const elapsed = Math.min(real, Math.max(real - STACKER_LAG_MS, Number.isFinite(claimedElapsed) ? claimedElapsed : real));
  const dropped = { left: stackerPosition(level, s.width, elapsed), width: s.width };
  const placed = stackerOverlap(s.rows[s.rows.length - 1] ?? null, dropped);
  if (placed.width === 0) return { ...s, status: "over", last: dropped, levelStartAt: now };
  const rows = [...s.rows, placed];
  const done = rows.length >= STACKER_LEVELS;
  return {
    ...s,
    rows,
    last: dropped,
    width: done ? placed.width : Math.min(placed.width, stackerMaxWidth(rows.length + 1)),
    levelStartAt: now,
    status: done ? "won" : "playing",
  };
}

export const StackerViewSchema = z.object({
  id: z.string(),
  rows: z.array(StackerRowSchema),
  /** The moving row (level = rows.length + 1). */
  width: z.number().int(),
  status: z.enum(["playing", "over", "won"]),
  last: StackerRowSchema.nullable(),
});
export type StackerView = z.infer<typeof StackerViewSchema>;

export function stackerView(s: StackerState): StackerView {
  return { id: s.id, rows: s.rows, width: s.width, status: s.status, last: s.last };
}

// ---------- requests and responses ----------

export const ArcadeBodySchema = z.discriminatedUnion("action", [
  z.object({ game: z.literal("mines"), action: z.literal("new") }),
  z.object({ game: z.literal("mines"), action: z.literal("reveal"), cell: z.number().int().min(0).max(MINES_CELLS - 1) }),
  z.object({ game: z.literal("mines"), action: z.literal("flag"), cell: z.number().int().min(0).max(MINES_CELLS - 1) }),
  z.object({ game: z.literal("stacker"), action: z.literal("start") }),
  z.object({ game: z.literal("stacker"), action: z.literal("drop"), id: z.string().max(40), at: z.number().min(0).max(3_600_000) }),
]);
export type ArcadeBody = z.infer<typeof ArcadeBodySchema>;

const ArcadeMeta = {
  /** Coins credited by this move. */
  reward: z.number().int().min(0),
  /** Your balance afterwards. */
  coins: z.number().int(),
};

export const ArcadeResponseSchema = z.discriminatedUnion("game", [
  z.object({ game: z.literal("mines"), view: MinesViewSchema, ...ArcadeMeta }),
  z.object({ game: z.literal("stacker"), view: StackerViewSchema, ...ArcadeMeta }),
]);
export type ArcadeResponse = z.infer<typeof ArcadeResponseSchema>;
